/**
 * Schools table column definitions for Product Insights · LAE (Schools mode). Shared by the page's
 * Schools table (C8) and the School modal's outcomes table (C12, through `createSchoolColumns({ groups })`).
 *
 * Ports LAE `features/productivity/shared/schoolColumns.jsx`. One row per school × funnel; every
 * stacked column draws one line per assignment, then a bold subtotal line (`./StackedCell`).
 *
 * Deliberate differences from LAE:
 * - No drill-downs (owner 2026-10-29): `onDrill`, `withDrill`, `ClickableCount` and `schoolCellScope`
 *   are gone, and every count is plain text.
 * - No rubric (spec §0.3): `useSchoolRubricColumns`, `Banded` and `useRowRubric` are gone.
 * - No definition tooltips (spec §0.4): the visible header is `short`, and `label` (the full name) is
 *   the header `title` and the ColumnManager label. The "lines can add up to more than the total"
 *   note lives in the guide only.
 * - Number conditions (owner decision F1): every numeric column carries `kind: 'number'` and a `get`
 *   that returns a number, or null / undefined. Durations filter in minutes, rounded to 1 dp.
 * - School cell: when the row maps to an EMERGE company (`emerge_company`), the primary name links to
 *   that company and the click stops there, so the row's open-modal click does not also fire. An
 *   unmapped school is plain text. The funnel is a neutral `Badge`.
 * - `schoolRowsOf` lives in `./lae-school-rows` (P6), not here.
 */
import type { ReactNode } from 'react';
import { Badge } from '@/components/ui';
import { TextLink } from '@/components/ui/TextLink';
import type { LaeProdAssignmentLine, LaeProdMetrics } from '@/lib/types';
import type { LaeSchoolRow } from './lae-school-rows';
import {
  AssignmentName,
  LineDate,
  LineDuration,
  LineNum,
  LinePct,
  SpecialistsLine,
  SpecialistsTotal,
  StackedCell,
  WorkedOf,
} from './StackedCell';
import { fmtDay, splitSchool } from './lae-format';

/** The §10.3 column contract shared by the Schools and Counsellors tables. */
export interface LaeColumn<Row> {
  /** Stored by ColumnManager: never rename. */
  key: string;
  /** Band id (Schools only). */
  group?: string;
  /** Full label: ColumnManager `labelOf` and the header `title`. */
  label: string;
  /** Visible header when shorter. */
  short?: string;
  /** Right-aligned, tabular. */
  numeric?: boolean;
  /** Number conditions in the column filter (owner decision F1); `get` then returns a number. */
  kind?: 'number';
  /** Filter value (minutes for durations). */
  get: (row: Row) => string | number | null | undefined;
  /** Sort value; nulls sort last. */
  sortValue: (row: Row) => string | number | null;
  /** Cell content, stacked for Schools. */
  render: (row: Row) => ReactNode;
  /** false = a rank column. */
  manageable?: boolean;
  defaultHidden?: boolean;
  /** min-w literals. */
  className?: string;
}

/** A Schools table row, as P6 `schoolRowsOf` builds it. */
export type SchoolTableRow = LaeSchoolRow;

export type SchoolColumn = LaeColumn<SchoolTableRow>;

export const SCHOOL_GROUPS = {
  assignment: 'Assignment',
  calls: 'Calls',
  interested: 'Interested in 2027 Intakes',
  positive: 'Positive changes',
  negative: 'Negative changes',
  outcome: 'Applications',
} as const;

export type SchoolGroupId = keyof typeof SCHOOL_GROUPS;

export const SCHOOL_OUTCOME_GROUPS: SchoolGroupId[] = ['interested', 'positive', 'negative', 'outcome'];

export const SCHOOL_OUTCOME_KEYS = [
  'counselling_completed',
  'ccm_talk_seconds',
  'avg_ccm_talk_seconds',
  'advanced',
  'adv_talk_seconds',
  'avg_adv_talk_seconds',
  'regressed',
  'neg_talk_seconds',
  'avg_neg_talk_seconds',
  'applications',
] as const;

/** The value one stacked line draws: an assignment line, the one-line merge, or the subtotal (the row). */
type StackedValue = (SchoolTableRow | LaeProdAssignmentLine) & {
  __total?: boolean;
  __single?: boolean;
  specialists?: LaeProdAssignmentLine['specialists'];
};

const asRecord = (v: unknown): Record<string, unknown> =>
  v !== null && typeof v === 'object' ? (v as Record<string, unknown>) : {};

/** A finite number, else null (blank, missing and junk all read as "no value", never 0). */
function numOf(v: unknown, key: string): number | null {
  const x = asRecord(v)[key];
  if (x === null || x === undefined || x === '') return null;
  const n = typeof x === 'number' ? x : Number(x);
  return Number.isFinite(n) ? n : null;
}

/** Seconds → minutes at 1 dp, for the duration filters. */
function minutesOf(row: SchoolTableRow, key: string): number | null {
  const s = numOf(row, key);
  return s === null ? null : Math.round(s / 6) / 10;
}

const markTotal = (r: unknown): StackedValue => ({ ...(asRecord(r) as unknown as StackedValue), __total: true });

/** A stacked body cell: `get` hands the whole line through, the subtotal is the row marked `__total`. */
const stacked =
  (fmt: (v: StackedValue) => ReactNode) =>
  (row: SchoolTableRow): ReactNode => (
    <StackedCell row={row} get={(a: unknown) => a} total={markTotal} fmt={(v: unknown) => fmt(v as StackedValue)} />
  );

const linesFor = (r: SchoolTableRow): LaeProdAssignmentLine[] =>
  r.__allLines ?? (Array.isArray(r.assignments) ? r.assignments : []);

function numCol(
  key: string,
  group: SchoolGroupId,
  short: string,
  label: string,
  draw: (value: number | null) => ReactNode,
): SchoolColumn {
  return {
    key,
    group,
    short,
    label,
    numeric: true,
    kind: 'number',
    get: (r) => numOf(r, key),
    sortValue: (r) => numOf(r, key),
    render: stacked((v) => draw(numOf(v, key))),
  };
}

function durationCol(key: string, group: SchoolGroupId, short: string, label: string, avg: boolean): SchoolColumn {
  return {
    ...numCol(key, group, short, label, (v) => <LineDuration value={v} avg={avg} />),
    get: (r) => minutesOf(r, key),
  };
}

const count = (key: string, group: SchoolGroupId, short: string, label: string): SchoolColumn =>
  numCol(key, group, short, label, (v) => <LineNum value={v} />);

/**
 * The school cell: not stacked. The primary name links to the mapped EMERGE company, if any.
 * A plain render function, not a component, so this module stays fast-refresh clean.
 */
function schoolCell(row: SchoolTableRow): ReactNode {
  const name = row.school_name ?? '';
  const { primary, secondary } = splitSchool(name);
  const company = row.emerge_company;
  const funnel = row.funnel_label || row.funnel;
  return (
    <div className="min-w-[168px] max-w-[220px] leading-6">
      <div className="truncate font-medium text-fg" title={name || undefined}>
        {company ? (
          <TextLink to={`/companies/${company.id}`} onClick={(e) => e.stopPropagation()}>
            {primary || name || '—'}
          </TextLink>
        ) : (
          primary || name || '—'
        )}
      </div>
      {secondary ? <div className="truncate text-xs text-fg-muted">{secondary}</div> : null}
      {funnel ? (
        <div className="mt-1 max-w-full">
          <Badge tone="neutral" title={funnel}>
            {funnel}
          </Badge>
        </div>
      ) : null}
    </div>
  );
}

const ALL_COLUMNS: SchoolColumn[] = [
  {
    key: 'school',
    label: 'School',
    get: (r) => r.school_name ?? '',
    sortValue: (r) => {
      const name = r.school_name ?? '';
      return splitSchool(name).primary || name;
    },
    render: schoolCell,
  },
  {
    key: 'assignment',
    group: 'assignment',
    label: 'Assignment',
    short: 'Assignment',
    get: (r) =>
      linesFor(r)
        .map((a) => a.name || a.assignment_type)
        .filter(Boolean)
        .join(', '),
    sortValue: (r) => linesFor(r).length,
    render: stacked((v) => {
      if (v.__total) {
        const all = (v as SchoolTableRow).__allLines;
        return all ? `${all.length} ${all.length === 1 ? 'assignment' : 'assignments'}` : 'Total';
      }
      return (
        <span className="block max-w-[180px]">
          <AssignmentName line={v} />
        </span>
      );
    }),
  },
  {
    key: 'start_date',
    group: 'assignment',
    label: 'Start Date',
    short: 'Start',
    numeric: true,
    get: (r) => (r.start_date ? fmtDay(r.start_date) : null),
    sortValue: (r) => r.start_date ?? null,
    render: stacked((v) => <LineDate value={v.start_date ?? null} />),
  },
  {
    key: 'end_date',
    group: 'assignment',
    label: 'End Date',
    short: 'End',
    numeric: true,
    get: (r) => (r.end_date ? fmtDay(r.end_date) : null),
    sortValue: (r) => r.end_date ?? null,
    render: stacked((v) => <LineDate value={v.end_date ?? null} />),
  },
  {
    key: 'specialists',
    group: 'assignment',
    label: 'Specialists',
    short: 'Specialists',
    numeric: true,
    kind: 'number',
    get: (r) => numOf(r, 'specialists_worked'),
    sortValue: (r) => numOf(r, 'specialists_worked'),
    render: stacked((v) =>
      v.__total ? <SpecialistsTotal row={v as SchoolTableRow} /> : <SpecialistsLine line={v} />,
    ),
  },
  {
    key: 'worked',
    group: 'calls',
    label: 'Leads Worked On / Records',
    short: 'Leads worked',
    numeric: true,
    kind: 'number',
    get: (r) => numOf(r, 'worked'),
    sortValue: (r) => numOf(r, 'worked'),
    render: stacked((v) => <WorkedOf worked={numOf(v, 'worked')} records={numOf(v, 'allocated')} />),
  },
  count('calls', 'calls', 'Dialled', 'Total Calls Dialled'),
  numCol('connect_rate', 'calls', 'Answer rate', 'Answer Rate', (v) => <LinePct value={v} />),
  durationCol('call_seconds', 'calls', 'Total time', 'Total Call Time', false),
  durationCol('avg_talk_seconds', 'calls', 'Avg time', 'Avg Call Time', true),
  count('counselling_completed', 'interested', 'Marked', 'Marked as Interested in 2027 Intakes'),
  durationCol('ccm_talk_seconds', 'interested', 'Total time', 'Total Call Time - Interested in 2027 Intakes', false),
  durationCol('avg_ccm_talk_seconds', 'interested', 'Avg time', 'Avg Call Time - Interested in 2027 Intakes', true),
  count('advanced', 'positive', 'Changes', 'Positive Status Changes'),
  durationCol('adv_talk_seconds', 'positive', 'Total time', 'Total Call Time - Positive Changes', false),
  durationCol('avg_adv_talk_seconds', 'positive', 'Avg time', 'Avg Call Time - Positive Changes', true),
  count('regressed', 'negative', 'Changes', 'Negative Status Changes'),
  durationCol('neg_talk_seconds', 'negative', 'Total time', 'Total Call Time - Negative Changes', false),
  durationCol('avg_neg_talk_seconds', 'negative', 'Avg time', 'Avg Call Time - Negative Changes', true),
  count('applications', 'outcome', 'Started', 'Applications'),
];

/** The Schools columns in §5.3 order. `groups` keeps `school` plus only the named bands. */
export function createSchoolColumns({ groups }: { groups?: readonly string[] } = {}): SchoolColumn[] {
  if (!groups || groups.length === 0) return ALL_COLUMNS.slice();
  return ALL_COLUMNS.filter((c) => c.key === 'school' || (c.group !== undefined && groups.includes(c.group)));
}

/**
 * Footer cells for a totals object, keyed by column key. Assignment, Start, End and Specialists are
 * left out (blank). Uses the same renderers as the subtotal lines.
 */
export function schoolFooterCells(
  totals: Partial<LaeProdMetrics> | null | undefined,
): Partial<Record<string, ReactNode>> {
  if (!totals) return {};
  const n = (key: string) => numOf(totals, key);
  return {
    school: <span className="font-semibold text-fg">Total</span>,
    worked: <WorkedOf worked={n('worked')} records={n('allocated')} />,
    calls: <LineNum value={n('calls')} />,
    connect_rate: <LinePct value={n('connect_rate')} />,
    call_seconds: <LineDuration value={n('call_seconds')} />,
    avg_talk_seconds: <LineDuration value={n('avg_talk_seconds')} avg />,
    counselling_completed: <LineNum value={n('counselling_completed')} />,
    ccm_talk_seconds: <LineDuration value={n('ccm_talk_seconds')} />,
    avg_ccm_talk_seconds: <LineDuration value={n('avg_ccm_talk_seconds')} avg />,
    advanced: <LineNum value={n('advanced')} />,
    adv_talk_seconds: <LineDuration value={n('adv_talk_seconds')} />,
    avg_adv_talk_seconds: <LineDuration value={n('avg_adv_talk_seconds')} avg />,
    regressed: <LineNum value={n('regressed')} />,
    neg_talk_seconds: <LineDuration value={n('neg_talk_seconds')} />,
    avg_neg_talk_seconds: <LineDuration value={n('avg_neg_talk_seconds')} avg />,
    applications: <LineNum value={n('applications')} />,
  };
}
